// src/pages/users/CreateRolePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Users, Shield, ChevronDown, ChevronUp, Check, X, AlertCircle } from 'lucide-react';
import { createUserRole } from '../../services/api/users';
import { getPermissions } from '../../services/api/permissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './CreateRolePage.module.css';

const CreateRolePage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  const [formData, setFormData] = useState({
    name: '',
    remarks: '',
    permissionKeys: []
  });
  
  const [permissions, setPermissions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  // Fetch permissions on component mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoadingPermissions(true);
        const response = await getPermissions();
        setPermissions(response);
        
        // Expand all groups by default
        const initialExpanded = {};
        Object.keys(response).forEach(group => {
          initialExpanded[group] = true;
        });
        setExpandedGroups(initialExpanded);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setErrors({ permissions: t('userRoles.failedToLoadPermissions') });
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [t]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('userRoles.roleNameRequired');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('userRoles.roleNameMinLength');
    }

    if (formData.permissionKeys.length === 0) {
      newErrors.permissions = t('userRoles.selectAtLeastOne');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionToggle = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      permissionKeys: prev.permissionKeys.includes(permissionKey)
        ? prev.permissionKeys.filter(key => key !== permissionKey)
        : [...prev.permissionKeys, permissionKey]
    }));

    // Clear permissions error if user selects any permission
    if (errors.permissions) {
      setErrors(prev => ({
        ...prev,
        permissions: ''
      }));
    }
  };

  const handleGroupToggle = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleSelectAllInGroup = (groupName) => {
    const groupPermissions = permissions[groupName] || [];
    const groupKeys = groupPermissions.map(p => p.key);
    const allSelected = groupKeys.every(key => formData.permissionKeys.includes(key));

    setFormData(prev => ({
      ...prev,
      permissionKeys: allSelected
        ? prev.permissionKeys.filter(key => !groupKeys.includes(key))
        : [...new Set([...prev.permissionKeys, ...groupKeys])]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await createUserRole({
        name: formData.name.trim(),
        remarks: formData.remarks.trim() || undefined,
        permissionKeys: formData.permissionKeys
      });

      success(t('userRoles.roleCreatedSuccess', { roleName: response.role.name }), {
        title: t('common.success'),
        duration: 4000
      });
      navigate('/users/roles');
    } catch (error) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: t('userRoles.failedToCreate') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedCount = () => formData.permissionKeys.length;
  const getTotalPermissions = () => {
    return Object.values(permissions).reduce((total, group) => total + group.length, 0);
  };

  const isGroupFullySelected = (groupName) => {
    const groupPermissions = permissions[groupName] || [];
    return groupPermissions.length > 0 && 
           groupPermissions.every(p => formData.permissionKeys.includes(p.key));
  };

  const isGroupPartiallySelected = (groupName) => {
    const groupPermissions = permissions[groupName] || [];
    return groupPermissions.some(p => formData.permissionKeys.includes(p.key)) &&
           !isGroupFullySelected(groupName);
  };

  if (isLoadingPermissions) {
    return (
      <div className={styles.createRolePage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <button 
                onClick={() => navigate('/users/roles')} 
                className={styles.backButton}
              >
                <ArrowLeft size={20} />
                {t('userRoles.backToRoles')}
              </button>
            </div>
            <div className={styles.headerTitle}>
              <Users className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('userRoles.createNewRole')}</h1>
                <p>{t('userRoles.definePermissions')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('userRoles.loadingPermissions')}</h3>
            <p>{t('userRoles.loadingPermissionsDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createRolePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Users className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('userRoles.createNewRole')}</h1>
              <p>{t('userRoles.definePermissions')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              onClick={() => navigate('/users/roles')} 
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('userRoles.backToRoles')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {(errors.submit || errors.permissions) && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              {errors.submit && <p>{errors.submit}</p>}
              {errors.permissions && <p>{errors.permissions}</p>}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.roleForm}>
          {/* Basic Information Card */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('userRoles.basicInformation')}</h2>
              <p>{t('userRoles.provideBasicDetails')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('userRoles.roleName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('userRoles.roleNamePlaceholder')}
                  className={errors.name ? 'error' : ''}
                  required
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="remarks">{t('userRoles.description')}</label>
                <textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  placeholder={t('userRoles.descriptionPlaceholder')}
                  rows="4"
                />
                <span className={styles.formHint}>{t('userRoles.descriptionHint')}</span>
              </div>
            </div>
          </div>

          {/* Permissions Card */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <div className={styles.headerLeft}>
                <Shield size={20} />
                <div>
                  <h2>{t('userRoles.permissions')}</h2>
                  <p>{t('userRoles.selectPermissions')}</p>
                </div>
              </div>
              <div className={styles.permissionsSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>{getSelectedCount()}</span>
                  <span className={styles.summaryLabel}>{t('userRoles.selected')}</span>
                </div>
                <div className={styles.summaryDivider}></div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>{getTotalPermissions()}</span>
                  <span className={styles.summaryLabel}>{t('userRoles.total')}</span>
                </div>
              </div>
            </div>

            <div className={styles.cardContent}>
              <div className={styles.permissionsContainer}>
                {Object.entries(permissions).map(([groupName, groupPermissions]) => (
                  <div key={groupName} className={styles.permissionGroup}>
                    <div className={styles.groupHeader}>
                      <button
                        type="button"
                        className={styles.groupToggle}
                        onClick={() => handleGroupToggle(groupName)}
                      >
                        <div className={styles.groupInfo}>
                          {expandedGroups[groupName] ? 
                            <ChevronUp size={18} /> : 
                            <ChevronDown size={18} />
                          }
                          <span className={styles.groupName}>{groupName}</span>
                          <span className={styles.groupCount}>
                            {groupPermissions.filter(p => formData.permissionKeys.includes(p.key)).length} / {groupPermissions.length}
                          </span>
                        </div>
                      </button>
                      
                      <button
                        type="button"
                        className={`${styles.selectAllButton} ${
                          isGroupFullySelected(groupName) ? styles.selected : 
                          isGroupPartiallySelected(groupName) ? styles.partial : ''
                        }`}
                        onClick={() => handleSelectAllInGroup(groupName)}
                      >
                        {isGroupFullySelected(groupName) ? t('common.deselectAll') : t('common.selectAll')}
                      </button>
                    </div>

                    {expandedGroups[groupName] && (
                      <div className={styles.permissionsList}>
                        {groupPermissions.map((permission) => (
                          <div key={permission.key} className={styles.permissionItem}>
                            <label className={styles.permissionCheckbox}>
                              <input
                                type="checkbox"
                                checked={formData.permissionKeys.includes(permission.key)}
                                onChange={() => handlePermissionToggle(permission.key)}
                              />
                              <span className={styles.checkmark}>
                                {formData.permissionKeys.includes(permission.key) && 
                                  <Check size={14} />
                                }
                              </span>
                              <div className={styles.permissionDetails}>
                                <span className={styles.permissionKey}>{permission.description}</span>
                                <span className={styles.permissionDescription}>
                                  {permission.key}
                                </span>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={() => navigate('/users/roles')}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {t('userRoles.createRole')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRolePage;